import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";
import Seo, { breadcrumbJsonLd } from "../components/Seo";
import { PAGE_SEO, SERVICES, WA_LINKS } from "../data/siteData";
import { ArrowUpRight } from "lucide-react";

const INDUSTRIES_WE_HELP = [
  "Restaurants", "Salons & Barbers", "Retail & Boutiques", "Hospitality & Tourism",
  "Professional Services", "Real Estate", "Contractors & Trades", "Schools & Education",
  "Medical Offices", "Event Companies", "Churches & Nonprofits", "Vacation Rentals",
];

export default function ServicesHub() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="services-hub-page" className="bg-white">
      <Seo
        {...PAGE_SEO.services}
        jsonLd={breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
        ])}
      />
      <Navbar />

      <section className="relative bg-[#0A192F] text-white pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(192,192,192,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(192,192,192,0.06) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
              AXIS Services
            </span>
          </div>
          <h1
            data-testid="services-hub-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold leading-[1.0] max-w-3xl"
          >
            Three focused services. <span className="text-[#D4AF37]">One clear recommendation.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#C0C0C0] leading-relaxed max-w-2xl">
            AXIS helps organizations solve communication, visibility, differentiation, and
            customer-experience problems through clearly scoped strategic and creative
            engagements.
          </p>
          <div className="mt-9">
            <a
              href={WA_LINKS.recommend}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-hub-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] px-7 py-3.5 text-sm md:text-base font-semibold border border-[#D4AF37] hover:bg-transparent hover:text-[#D4AF37] transition-all duration-300"
            >
              Get My AXIS Recommendation →
            </a>
          </div>
        </div>
      </section>

      {/* SERVICE PATHWAYS */}
      <section data-testid="services-hub-list" className="bg-[#F8F9FA] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-6">
          {SERVICES.map((s, i) => (
            <div
              key={s.slug}
              data-testid={`hub-service-${s.slug}`}
              className="bg-white border border-[#C0C0C0]/40 p-7 md:p-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center"
            >
              <div className="lg:col-span-8">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-2">
                  Service 0{i + 1}
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#0A192F]">
                  {s.name}
                </h2>
                <p className="mt-3 text-sm md:text-base text-[#475569] leading-relaxed max-w-2xl">
                  {s.summary}
                </p>
                <p className="mt-3 text-sm text-[#1E3A8A] font-medium">
                  Best for: {s.hubBestFor}.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                <div className="text-sm font-semibold text-[#0A192F]">{s.priceLine}</div>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`hub-service-cta-${s.slug}`}
                  className="inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white px-6 py-3 text-sm font-semibold border border-[#0A192F] hover:bg-transparent hover:text-[#0A192F] transition-all duration-300"
                >
                  View {s.shortName} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FIT GUIDANCE */}
      <section data-testid="services-fit-guidance" className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#C0C0C0]" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
                Fit Guidance
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-[#0A192F] leading-[1.1]">
              Which service should <span className="text-[#1E3A8A]">you start with?</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <div key={s.slug} className="bg-[#F8F9FA] border border-[#C0C0C0]/40 p-7">
                <h3 className="font-display text-lg font-semibold text-[#0A192F]">
                  {s.fitGuidance.title}
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {s.fitGuidance.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-[#475569]">
                      <span className="w-1.5 h-1.5 bg-[#D4AF37] rotate-45 mt-1.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES WE HELP */}
      <section data-testid="services-industries" className="bg-[#0A192F] text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
              Industries We Help
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            {INDUSTRIES_WE_HELP.map((ind) => (
              <span
                key={ind}
                className="border border-[#C0C0C0]/25 px-4 py-2 text-sm text-[#C0C0C0]"
              >
                {ind}
              </span>
            ))}
          </div>
          <p className="mt-6 text-sm text-[#94A3B8] max-w-2xl">
            Operator-led businesses of every kind. Not sure where you fit? Tell us the problem
            and we will recommend the right starting point.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
